import { useState, useEffect, useMemo } from "react";
import { Calendar } from "lucide-react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

const StudentProgressTracker = () => {
  const [students, setStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("All Courses");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const fetchStudentsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const coursesRef = collection(db, "courses");
      const coursesSnapshot = await getDocs(coursesRef);
      const courseMap = new Map();
      const coursesSet = new Set();
      coursesSnapshot.forEach((doc) => {
        const courseData = { id: doc.id, ...doc.data() };
        courseMap.set(doc.id, courseData);
        coursesSet.add(courseData.title);
      });

      const studentsRef = collection(db, "students");
      const studentsSnapshot = await getDocs(studentsRef);

      const studentsData = [];
      studentsSnapshot.forEach((studentDoc) => {
        const studentData = { id: studentDoc.id, ...studentDoc.data() };
        if (studentData.course && courseMap.has(studentData.course)) {
          const courseData = courseMap.get(studentData.course);
          const completedDays = studentData.current_day_status
            ? parseInt(studentData.current_day_status) - 1
            : 0;
          const totalDays = courseData.duration || 30;
          const percentage =
            totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;

          studentsData.push({
            student:
              studentData.name ||
              studentData.email ||
              `Student ${studentData.id}`,
            course: courseData.title,
            courseId: studentData.course,
            completedDays,
            totalDays,
            percentage,
          });
        }
      });

      setStudents(studentsData);
      setCourses([...coursesSet]);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load student data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentsData();
  }, []);

  const filteredStudents = useMemo(() => {
    if (selectedCourse === "All Courses") return students;
    return students.filter((student) => student.course === selectedCourse);
  }, [students, selectedCourse]);

  const paginatedStudents = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredStudents.slice(start, start + itemsPerPage);
  }, [filteredStudents, page]);

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const handleCourseChange = (event) => {
    setSelectedCourse(event.target.value);
    setPage(1);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "16rem",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              animation: "spin 1s linear infinite",
              borderRadius: "50%",
              height: "3rem",
              width: "3rem",
              borderWidth: "2px",
              borderStyle: "solid",
              borderColor: "transparent transparent #2563eb transparent",
              margin: "0 auto 1rem auto",
            }}
          ></div>
          <p style={{ color: "#4b5563", margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "0.5rem",
          padding: "1.5rem",
          textAlign: "center",
        }}
      >
        <p style={{ color: "#dc2626", margin: 0 }}>{error}</p>
        <button
          onClick={fetchStudentsData}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "#dc2626",
            color: "white",
            border: "none",
            borderRadius: "0.25rem",
            cursor: "pointer",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#b91c1c")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#dc2626")}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className="spt-container"
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "1.5rem",
        fontSize: "14px",
        color: "#111827",
      }}
    >
      <style>
        {`
          .spt-container * { box-sizing: border-box; }
          .spt-container table { border-collapse: collapse !important; width: 100% !important; }
          .spt-container th, .spt-container td { vertical-align: middle !important; }
          .spt-container button, .spt-container select { font-size: inherit !important; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .spt-hover:hover { background-color: #f9fafb !important; }
          .spt-button:disabled { opacity: 0.5 !important; cursor: not-allowed !important; }
          .spt-badge-completed {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.25rem 0.75rem;
            background-color: #e6fffa;
            color: #059669;
            border-radius: 9999px;
            font-size: 0.875rem;
            font-weight: 500;
            border: 1px solid #10b981;
          }
          .spt-badge-total {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.25rem 0.75rem;
            background-color: #f3f4f6;
            color: #374151;
            border-radius: 9999px;
            font-size: 0.875rem;
            font-weight: 500;
            border: 1px solid #d1d5db;
          }
          .spt-badge-total svg {
            width: 1rem;
            height: 1rem;
            margin-right: 0.25rem;
          }
          .spt-th {
            font-weight: 600 !important;
            border-bottom: 2px solid #e5e7eb !important;
          }
        `}
      </style>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "#111827",
              margin: 0,
            }}
          >
            Student Progress Tracker
          </h2>
          <p style={{ color: "#4b5563", margin: "0.25rem 0 0" }}>
            Track student progress across all courses
          </p>
        </div>
        <select
          style={{
            padding: "0.5rem 1rem",
            border: "1px solid #d1d5db",
            borderRadius: "0.5rem",
            maxWidth: "200px",
            backgroundColor: "white",
            color: "#111827",
          }}
          value={selectedCourse}
          onChange={handleCourseChange}
        >
          <option value="All Courses">All Courses</option>
          {courses.map((course) => (
            <option key={course} value={course}>
              {course}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          backgroundColor: "white",
          borderRadius: "0.5rem",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "1.5rem", borderBottom: "1px solid #e5e7eb" }}>
          <h3
            style={{
              fontSize: "1.125rem",
              fontWeight: "600",
              color: "#111827",
              margin: 0,
            }}
          >
            Student Progress Overview
          </h3>
          <p style={{ color: "#4b5563", margin: "0.25rem 0 0" }}>
            Track completed and total days for each enrolled student
          </p>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%" }}>
            <thead style={{ backgroundColor: "#f9fafb" }}>
              <tr>
                <th
                  className="spt-th"
                  style={{
                    padding: "0.75rem 1.5rem",
                    textAlign: "left",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Student
                </th>
                <th
                  className="spt-th"
                  style={{
                    padding: "0.75rem 1.5rem",
                    textAlign: "left",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Course
                </th>
                <th
                  className="spt-th"
                  style={{
                    padding: "0.75rem 1.5rem",
                    textAlign: "left",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Completed Days
                </th>
                <th
                  className="spt-th"
                  style={{
                    padding: "0.75rem 1.5rem",
                    textAlign: "left",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Total Days
                </th>
                <th
                  className="spt-th"
                  style={{
                    padding: "0.75rem 1.5rem",
                    textAlign: "left",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Progress
                </th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: "white" }}>
              {paginatedStudents.map((student, idx) => (
                <tr
                  key={idx}
                  className="spt-hover"
                  style={{ borderBottom: "1px solid #f3f4f6" }}
                >
                  <td style={{ padding: "1rem 1.5rem", whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      {student.student}
                    </div>
                  </td>
                  <td style={{ padding: "1rem 1.5rem", whiteSpace: "nowrap" }}>
                    <div style={{ fontSize: "0.875rem", color: "#111827" }}>
                      {student.course}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "1rem 1.5rem",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                    }}
                  >
                    <span className="spt-badge-completed">
                      {student.completedDays}
                    </span>
                  </td>

                  <td style={{ padding: "1rem 1.5rem", whiteSpace: "nowrap" }}>
                    <span className="spt-badge-total">
                      <Calendar />
                      {student.totalDays}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 1.5rem", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "100%",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "9999px",
                          height: "0.625rem",
                          marginRight: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#2563eb",
                            height: "0.625rem",
                            borderRadius: "9999px",
                            transition: "all 0.3s",
                            width: `${student.percentage}%`,
                          }}
                        ></div>
                      </div>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#111827",
                          minWidth: "3rem",
                        }}
                      >
                        {student.percentage}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: "1rem 1.5rem",
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: "0.875rem", color: "#374151" }}>
            Showing{" "}
            {filteredStudents.length === 0 ? 0 : (page - 1) * itemsPerPage + 1}{" "}
            to {Math.min(page * itemsPerPage, filteredStudents.length)} of{" "}
            {filteredStudents.length} students
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{
                padding: "0.25rem 0.75rem",
                fontSize: "0.875rem",
                border: "1px solid #d1d5db",
                borderRadius: "0.25rem",
                backgroundColor: page === 1 ? "#f9fafb" : "white",
                cursor: page === 1 ? "not-allowed" : "pointer",
                opacity: page === 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: "0.875rem", color: "#374151" }}>
              Page {page} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages || totalPages === 0}
              style={{
                padding: "0.25rem 0.75rem",
                fontSize: "0.875rem",
                border: "1px solid #d1d5db",
                borderRadius: "0.25rem",
                backgroundColor:
                  page === totalPages || totalPages === 0 ? "#f9fafb" : "white",
                cursor:
                  page === totalPages || totalPages === 0
                    ? "not-allowed"
                    : "pointer",
                opacity: page === totalPages || totalPages === 0 ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProgressTracker;
